import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Code, Smartphone, ShoppingCart, Briefcase, Palette, Rocket,
  CheckCircle, Star, User, Quote, Calendar, Clock, Send, MessageCircle,
  Phone, Mail, Globe, ChevronDown, ExternalLink, Calculator
} from "lucide-react";
import QuoteWizard from "../components/quote/QuoteWizard";
import QuoteStatsChip from "../components/quote/QuoteStatsChip";
import QuotePulse from "../components/quote/QuotePulse";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: "easeOut" }
  })
};

const SERVICES = [
  { 
    icon: ShoppingCart, 
    title: "E-Commerce Websites", 
    desc: "Complete online stores with payment integration, inventory management, and seamless user experience.",
    features: ["Payment Gateway Integration", "Product Management", "Order Tracking", "Mobile Responsive"]
  },
  { 
    icon: Briefcase, 
    title: "Corporate Websites", 
    desc: "Professional business websites that establish credibility and showcase your brand effectively.",
    features: ["Modern Design", "SEO Optimized", "Contact Forms", "Analytics Dashboard"]
  },
  { 
    icon: Code, 
    title: "SaaS Applications", 
    desc: "Scalable web applications with user authentication, dashboards, and subscription management.",
    features: ["User Authentication", "Admin Dashboard", "API Integration", "Subscription Billing"]
  },
  { 
    icon: Palette, 
    title: "Portfolio Websites", 
    desc: "Stunning portfolio sites to showcase your work and attract clients or employers.",
    features: ["Gallery Layouts", "Project Showcases", "Contact Integration", "Blog Section"]
  },
  { 
    icon: Smartphone, 
    title: "Mobile-First Design", 
    desc: "Responsive websites that look perfect on all devices - phones, tablets, and desktops.",
    features: ["Touch Optimized", "Fast Loading", "PWA Support", "Cross-Browser"]
  },
  { 
    icon: Rocket, 
    title: "Landing Pages", 
    desc: "High-converting landing pages designed to capture leads and drive conversions.",
    features: ["A/B Testing Ready", "Lead Capture", "Analytics", "Fast Performance"]
  }
];

const WHY_CHOOSE_US = [
  "Expert developers with 10+ years experience",
  "Modern tech stack (React, Node.js, AI integration)",
  "SEO-optimized and mobile-responsive designs",
  "Post-launch support and maintenance",
  "Transparent pricing with no hidden costs",
  "Quick turnaround times"
];

const TIME_SLOTS = [
  "10:00 AM - 11:00 AM",
  "11:00 AM - 12:00 PM",
  "2:00 PM - 3:00 PM",
  "3:00 PM - 4:00 PM",
  "4:00 PM - 5:00 PM",
  "6:00 PM - 7:00 PM"
];

const PROJECT_TYPES = [
  "E-Commerce Website",
  "Corporate Website",
  "SaaS Application",
  "Portfolio Website",
  "Landing Page",
  "Custom Web Application",
  "Website Redesign",
  "Other"
];

export default function WebsiteServices() {
  const [testimonials, setTestimonials] = useState([]);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_type: "",
    preferred_date: "",
    preferred_time: "",
    description: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    // Fetch website testimonials
    axios.get(`${API}/testimonials/website`).then(res => {
      setTestimonials(res.data);
    }).catch(() => {});
    
    // Fetch website projects
    axios.get(`${API}/website-projects/featured`).then(res => {
      setProjects(res.data);
    }).catch(() => {});
  }, []);

  // Get minimum date (tomorrow)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Get maximum date (30 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const res = await axios.post(`${API}/consultation/book`, formData);
      setSubmitStatus({ type: "success", message: res.data.message });
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        project_type: "",
        preferred_date: "",
        preferred_time: "",
        description: ""
      });
    } catch (err) {
      setSubmitStatus({ 
        type: "error", 
        message: err.response?.data?.detail || "Failed to book consultation. Please try again." 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent("Hi, I'm interested in website development services. I'd like to discuss my project.");
    window.open(`https://wa.me/919962536379?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <QuotePulse />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft size={20} />
            <span className="font-dm-sans">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] rounded-lg flex items-center justify-center">
              <Globe size={18} className="text-white" />
            </div>
            <span className="font-outfit font-bold text-white">ShansoAI</span>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div variants={fadeUp} initial="hidden" animate="visible">
            <span className="text-[#00F0FF] text-sm sm:text-base font-bold tracking-widest uppercase font-dm-sans">Website Development Services</span>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-white mt-4 leading-tight">
              Transform Your Ideas Into
              <span className="bg-gradient-to-r from-[#00F0FF] to-[#7000FF] bg-clip-text text-transparent"> Digital Reality</span>
            </h1>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mt-6 font-dm-sans">
              We build stunning, high-performance websites that drive results. From e-commerce to SaaS applications, we bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <a href="#get-quote" data-testid="hero-get-quote-btn" className="px-8 py-3 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white font-bold hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2">
                <Calculator size={18} />
                Submit Website Requirement
              </a>
              <a href="#consultation" className="px-8 py-3 bg-zinc-800 border border-zinc-700 text-white font-bold hover:bg-zinc-700 transition-colors">
                Book Free Consultation
              </a>
              <button
                onClick={openWhatsApp}
                className="px-8 py-3 bg-green-500 text-white font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={18} />
                Chat on WhatsApp
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-6 border-t border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Our Services</span>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">What We Build</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((service, idx) => (
              <motion.div
                key={idx}
                custom={idx}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-zinc-900/50 border border-zinc-800 p-6 hover:border-[#00F0FF]/40 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-[#00F0FF]/20 to-[#7000FF]/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-[#00F0FF]/30 group-hover:to-[#7000FF]/30 transition-all">
                  <service.icon size={24} className="text-[#00F0FF]" />
                </div>
                <h3 className="font-outfit font-bold text-xl text-white mb-2">{service.title}</h3>
                <p className="text-[#A1A1AA] text-sm font-dm-sans mb-4">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-zinc-400 text-sm">
                      <CheckCircle size={14} className="text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Website Requirement — Wizard */}
      <section id="get-quote" className="py-20 px-6 border-t border-zinc-800" style={{ background: "linear-gradient(180deg, rgba(34,211,238,0.04), transparent 60%)" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Tell Us About Your Project</span>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">Share your requirement in 60 seconds</h2>
            <p className="text-zinc-400 mt-3 max-w-2xl mx-auto text-sm sm:text-base">
              Answer a few quick questions, attach a brief if you have one, and our team will email you a detailed, custom quote tailored to your project within 24 hours.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-400"/> No upfront commitment</span>
              <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-400"/> Custom quote in 24 hours</span>
              <span className="flex items-center gap-1"><CheckCircle size={12} className="text-emerald-400"/> Free consultation call</span>
            </div>
            <QuoteStatsChip />
          </motion.div>

          <QuoteWizard />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 border-t border-zinc-800 bg-zinc-900/30">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Why Choose Us</span>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3 mb-6">
                Building Websites That Drive Results
              </h2>
              <p className="text-[#A1A1AA] font-dm-sans mb-8">
                We don't just build websites – we create digital experiences that convert visitors into customers. 
                Our team combines technical expertise with creative design to deliver solutions that exceed expectations.
              </p>
              <ul className="space-y-4">
                {WHY_CHOOSE_US.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#00F0FF] to-[#7000FF] flex items-center justify-center flex-shrink-0">
                      <CheckCircle size={14} className="text-white" />
                    </div>
                    <span className="text-white font-dm-sans">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              variants={fadeUp} 
              initial="hidden" 
              whileInView="visible" 
              viewport={{ once: true }}
              className="bg-zinc-900/50 border border-zinc-800 p-8"
            >
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-[#00F0FF] to-[#7000FF] rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Rocket size={36} className="text-white" />
                </div>
                <h3 className="font-outfit font-bold text-2xl text-white mb-4">Ready to Start?</h3>
                <p className="text-[#A1A1AA] font-dm-sans mb-6">
                  Get a free consultation and let's discuss how we can bring your project to life.
                </p>
                <a href="#consultation" className="inline-block px-8 py-3 bg-[#00F0FF] text-black font-bold hover:bg-white transition-colors">
                  Book Consultation
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Portfolio / Our Work Section */}
      {projects.length > 0 && (
        <section className="py-20 px-6 border-t border-zinc-800">
          <div className="max-w-6xl mx-auto">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
              <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Our Portfolio</span>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">Websites We've Built</h2>
              <p className="text-[#A1A1AA] font-dm-sans mt-4 max-w-2xl mx-auto">
                Take a look at some of the amazing websites we've created for our clients
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, idx) => (
                <motion.div
                  key={project.id}
                  custom={idx}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-zinc-900/50 border border-zinc-800 overflow-hidden group hover:border-[#00F0FF]/40 transition-all duration-300"
                >
                  {/* Screenshot */}
                  <div className="h-48 bg-gradient-to-br from-[#00F0FF]/10 to-[#7000FF]/10 overflow-hidden relative">
                    {project.screenshot_url ? (
                      <img 
                        src={project.screenshot_url} 
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Globe size={48} className="text-zinc-600" />
                      </div>
                    )}
                    {/* Type badge */}
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 bg-[#00F0FF] text-black text-xs font-bold">
                        {project.project_type === 'e-commerce' ? 'E-Commerce' :
                         project.project_type === 'corporate' ? 'Corporate' :
                         project.project_type === 'saas' ? 'SaaS' :
                         project.project_type === 'portfolio' ? 'Portfolio' :
                         project.project_type === 'landing-page' ? 'Landing Page' :
                         'Custom'}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-outfit font-bold text-lg text-white mb-1">{project.title}</h3>
                    <p className="text-[#A1A1AA] text-sm font-dm-sans mb-3">Client: {project.client_name}</p>
                    <p className="text-zinc-500 text-sm font-dm-sans line-clamp-2 mb-4">{project.description}</p>
                    
                    {project.technologies && (
                      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-4">
                        <Code size={14} />
                        <span className="truncate">{project.technologies}</span>
                      </div>
                    )}

                    {project.project_url && (
                      <a
                        href={project.project_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#00F0FF] text-sm font-bold hover:text-white transition-colors"
                      >
                        Visit Website <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 px-6 border-t border-zinc-800">
          <div className="max-w-6xl mx-auto">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
              <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Testimonials</span>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">What Our Clients Say</h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((testimonial, idx) => (
                <motion.div
                  key={testimonial.id}
                  custom={idx}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="bg-zinc-900/50 border border-zinc-800 p-6 relative"
                >
                  <Quote size={32} className="absolute top-4 right-4 text-[#00F0FF]/20" />
                  <div className="flex items-center gap-4 mb-4">
                    {testimonial.photo_url ? (
                      <img src={testimonial.photo_url} alt={testimonial.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#00F0FF]/30" />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#7000FF] flex items-center justify-center">
                        <User size={24} className="text-white" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-outfit font-bold text-white">{testimonial.name}</h4>
                      {(testimonial.role || testimonial.company) && (
                        <p className="text-[#A1A1AA] text-sm font-dm-sans">
                          {testimonial.role}{testimonial.role && testimonial.company && " at "}{testimonial.company}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} size={16} className={star <= testimonial.rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"} />
                    ))}
                  </div>
                  <p className="text-[#A1A1AA] text-sm font-dm-sans leading-relaxed">"{testimonial.review_text}"</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Consultation Booking Form */}
      <section id="consultation" className="py-20 px-6 border-t border-zinc-800 bg-zinc-900/30">
        <div className="max-w-4xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
            <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">Book a Consultation</span>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-white mt-3">Let's Discuss Your Project</h2>
            <p className="text-[#A1A1AA] font-dm-sans mt-4">Fill out the form below and we'll get back to you within 24 hours.</p>
          </motion.div>

          <motion.form 
            onSubmit={handleSubmit}
            variants={fadeUp} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true }}
            className="bg-zinc-900/50 border border-zinc-800 p-8"
          >
            {submitStatus && (
              <div className={`mb-6 p-4 rounded ${submitStatus.type === "success" ? "bg-green-500/20 border border-green-500/50 text-green-400" : "bg-red-500/20 border border-red-500/50 text-red-400"}`}>
                {submitStatus.message}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-white mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-2">Phone *</label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors"
                  placeholder="+91 9876543210"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-2">Company (Optional)</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors"
                  placeholder="Your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-2">Project Type *</label>
                <select
                  required
                  value={formData.project_type}
                  onChange={(e) => setFormData({ ...formData, project_type: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors"
                >
                  <option value="">Select project type</option>
                  {PROJECT_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-white mb-2">Preferred Date *</label>
                <div className="relative">
                  <input
                    type="date"
                    required
                    min={getMinDate()}
                    max={getMaxDate()}
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors [color-scheme:dark]"
                  />
                  <Calendar size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-white mb-2">Preferred Time *</label>
                <div className="relative">
                  <input
                    type="time"
                    required
                    min="09:00"
                    max="18:00"
                    value={formData.preferred_time}
                    onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors [color-scheme:dark]"
                  />
                  <Clock size={18} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                </div>
                <p className="text-zinc-500 text-xs mt-1">Available between 9:00 AM - 6:00 PM</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-white mb-2">Project Description *</label>
                <textarea
                  required
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white focus:border-[#00F0FF] focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your project, goals, and any specific requirements..."
                />
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 py-4 bg-gradient-to-r from-[#00F0FF] to-[#7000FF] text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? "Booking..." : <>Book Consultation <Send size={18} /></>}
              </button>
              <button
                type="button"
                onClick={openWhatsApp}
                className="flex-1 py-4 bg-green-500 text-white font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle size={18} />
                Prefer WhatsApp?
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* Contact Info Footer */}
      <section className="py-12 px-6 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="font-outfit font-bold text-xl text-white mb-6">Get in Touch</h3>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="mailto:support@shansoai.com" className="flex items-center justify-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors">
              <Mail size={18} />
              support@shansoai.com
            </a>
            <a href="tel:+919962536379" className="flex items-center justify-center gap-2 text-[#A1A1AA] hover:text-[#00F0FF] transition-colors">
              <Phone size={18} />
              +91 9962536379
            </a>
            <button onClick={openWhatsApp} className="flex items-center justify-center gap-2 text-green-400 hover:text-green-300 transition-colors">
              <MessageCircle size={18} />
              WhatsApp Chat
            </button>
          </div>
        </div>
      </section>

      {/* Floating WhatsApp Button - LEFT SIDE (AI Chatbot is on right) */}
      <button
        onClick={openWhatsApp}
        className="fixed bottom-6 left-6 w-14 h-14 bg-green-500 rounded-full shadow-lg flex items-center justify-center hover:bg-green-600 transition-colors z-40 group"
        title="Chat on WhatsApp"
      >
        <MessageCircle size={28} className="text-white" />
        <span className="absolute left-full ml-3 bg-zinc-900 text-white text-sm px-3 py-1.5 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
          WhatsApp
        </span>
      </button>
    </div>
  );
}
